import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, AlertCircle, Clock, Zap, Database, Shield, Users, Code } from 'lucide-react';

const Changelog = () => {
  const statusItems = [
    {
      status: 'operational',
      name: 'Lead Management API',
      description: 'All systems operational',
    },
    {
      status: 'operational',
      name: 'Data Validation Services',
      description: 'All systems operational',
    },
    {
      status: 'operational',
      name: 'Dashboard & Analytics',
      description: 'All systems operational',
    },
    {
      status: 'operational',
      name: 'Integrations',
      description: 'All systems operational',
    },
  ];

  const changelogEntries = [
    {
      date: 'Jan 10, 2026',
      entries: [
        {
          title: 'New Integration: Hunter.io Email Verification',
          category: 'Integrations',
          categoryColor: 'bg-purple-100 text-purple-700',
          icon: Zap,
          description: 'We are excited to announce that AALIGN now supports Hunter.io integration for email verification. Connect your Hunter.io account to automatically verify and enrich lead email addresses, ensuring higher deliverability rates and cleaner data.',
          highlights: [
            'Automatic email verification for all incoming leads',
            'Real-time email enrichment with verified contacts',
            'Reduced bounce rates and improved campaign performance',
          ],
        },
      ],
    },
    {
      date: 'Jan 06, 2026',
      entries: [
        {
          title: 'Enhanced Lead Validation with AI-Powered Scoring',
          category: 'Platform',
          categoryColor: 'bg-blue-100 text-blue-700',
          icon: Shield,
          description: 'Our lead validation system now includes AI-powered scoring to help you prioritize high-quality leads. The new scoring algorithm analyzes multiple data points including engagement history, company size, and industry relevance.',
          highlights: [
            'AI-driven lead quality scores from 0-100',
            'Customizable scoring criteria based on your business needs',
            'Automatic lead prioritization in your dashboard',
          ],
        },
      ],
    },
    {
      date: 'Dec 28, 2025',
      entries: [
        {
          title: 'Platform API v2.0 Released',
          category: 'API',
          categoryColor: 'bg-green-100 text-green-700',
          icon: Code,
          description: 'We have released version 2.0 of the AALIGN Platform API with improved performance, new endpoints, and enhanced documentation. The new API includes batch processing capabilities and webhook support for real-time notifications.',
          highlights: [
            'Batch lead upload supporting up to 10,000 leads per request',
            'Webhook notifications for lead status changes',
            'Improved rate limits: 1,000 requests per minute',
            'New filtering and sorting options for lead queries',
          ],
        },
      ],
    },
    {
      date: 'Dec 20, 2025',
      entries: [
        {
          title: 'Dashboard Analytics Overhaul',
          category: 'Dashboard',
          categoryColor: 'bg-orange-100 text-orange-700',
          icon: Database,
          description: 'The AALIGN dashboard has been completely redesigned with new analytics capabilities. Track your lead generation performance with enhanced visualizations, custom date ranges, and exportable reports.',
          highlights: [
            'New interactive charts and graphs',
            'Custom date range selection',
            'One-click report exports (PDF, CSV, Excel)',
            'Real-time campaign performance metrics',
          ],
        },
      ],
    },
    {
      date: 'Dec 15, 2025',
      entries: [
        {
          title: 'Multi-User Team Collaboration Features',
          category: 'Platform',
          categoryColor: 'bg-blue-100 text-blue-700',
          icon: Users,
          description: 'AALIGN now supports team collaboration with role-based access control. Invite team members, assign roles, and manage permissions to streamline your lead management workflow across your organization.',
          highlights: [
            'Role-based access control (Admin, Manager, Viewer)',
            'Team activity logs and audit trails',
            'Shared campaign templates',
            'Collaborative lead assignment and tracking',
          ],
        },
      ],
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'outage':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-black mb-4">Changelog</h1>
          <p className="text-xl text-gray-600">
            New updates and improvements at AALIGN.
          </p>
        </div>
      </section>

      {/* System Status */}
      <section className="py-8 px-6 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="font-semibold text-green-700">All Systems Operational</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {statusItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                {getStatusIcon(item.status)}
                <div>
                  <p className="font-medium text-gray-900">{item.name}</p>
                  <p className="text-sm text-gray-500">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Changelog Entries */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {changelogEntries.map((dateGroup, dateIdx) => (
            <div key={dateIdx} className="mb-12">
              {/* Date Header */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-lg font-bold text-gray-900">{dateGroup.date}</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* Entries for this date */}
              {dateGroup.entries.map((entry, entryIdx) => {
                const Icon = entry.icon;
                return (
                  <div key={entryIdx} className="bg-white rounded-xl border border-gray-200 p-6 mb-4 hover:shadow-md transition-shadow">
                    {/* Category Badge */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${entry.categoryColor}`}>
                            {entry.category}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{entry.title}</h3>
                        <p className="text-gray-600 leading-relaxed mb-4">{entry.description}</p>
                        
                        {/* Highlights */}
                        {entry.highlights && entry.highlights.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="font-semibold text-gray-700 mb-2">Key highlights:</p>
                            <ul className="space-y-2">
                              {entry.highlights.map((highlight, hIdx) => (
                                <li key={hIdx} className="flex items-start gap-2 text-gray-600">
                                  <span className="text-orange-500 mt-1">•</span>
                                  <span>{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Changelog;
